// ── Portfolio Mark ──────────────────────────────────────────────────────────
//
// What a buyer would pay for each loan, and for the book, per 100 of par.
// The Mark tab of Portfolio Performance.
//
//   price (clean) = (PV of the loan's projected net cash − accrued) / par × 100
//
// The cash is the performance run's own frozen projection — the current leg's
// projected flows — so the mark and the IRRs always come from the same run.
// It is discounted at forward SOFR (the run's curve) plus a spread for the
// loan's risk rating, or the loan's own override. The arithmetic is
// shared/mark.math.mjs, which is tested to agree with the Mark Proof workbook
// (doc/Portfolio_Mark_Proof.xlsx) to six decimal places.
//
// THE SPREADS ARE THE MARK. Anyone can type spreads here to see what the
// book would be worth; only an administrator can SAVE them, because the saved
// grid is what the book is said to be worth.
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useEffect, useMemo } from "react";
import { B, INK_MUTED, INK_PRIMARY, SURFACE, SURFACE_MUTED,
         HAIRLINE, HAIRLINE_FIRM } from "../../../shared/theme";
import { Card, Spinner, ErrorMsg, Btn, SearchBar, PanelTitle } from "../../../shared/ui";
import { fmtDate, exportCSV } from "../../../shared/format";
import { priceBook, SIZE_BUCKETS, COLLATERAL_PROFILES } from "../../../shared/mark.math.mjs";

export const MARK_RATINGS = ["Unrated", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];
// Placeholders, the same as the SQL seed and the workbook. Used only if the
// spread tables have not been created yet.
const DEFAULT_GRID = { Unrated:700, "1":500, "2":525, "3":550, "4":600, "5":650,
                       "6":750, "7":900, "8":1100, "9":1400, "10":1800 };
const SHIFTS = [-200, -100, -50, 0, 50, 100, 200];
// Size and collateral adjustments, added to the rating spread. Placeholders,
// as seeded by 2026-11-03_portfolio_mark_adjustments.sql.
const DEFAULT_ADJ = {
  size: { "<5mm":100, "5-20mm":0, ">20mm":-100, "Unknown":0 },
  collateral: { "AR-backed":-50, "Mixed":0, "Inventory-heavy":50, "Cash flow":150, "No collateral data":0 },
};
const ZERO_ADJ = {
  size: Object.fromEntries(SIZE_BUCKETS.map(b => [b, 0])),
  collateral: Object.fromEntries(COLLATERAL_PROFILES.map(b => [b, 0])),
};
const SIZE_LABEL = { "<5mm":"Under $5mm", "5-20mm":"$5–20mm", ">20mm":"Over $20mm", "Unknown":"No commitment" };

const money = n => (n == null || !isFinite(n)) ? "—"
  : (n < 0 ? "(" : "") + "$" + Math.abs(n).toLocaleString("en-US", { maximumFractionDigits: 0 }) + (n < 0 ? ")" : "");
const price = n => (n == null || !isFinite(n)) ? "—" : n.toFixed(3);
const pct = n => (n == null || !isFinite(n)) ? "—" : (n * 100).toFixed(2) + "%";
const yrs = n => (n == null || !isFinite(n)) ? "—" : n.toFixed(2);
const isBps = v => v !== "" && v !== null && v !== undefined && Number.isInteger(Number(v));

export const MarkPanel = ({ api, run, canSave }) => {
  const [data, setData]   = useState(null);
  const [err, setErr]     = useState("");
  const [busy, setBusy]   = useState(true);
  const [grid, setGrid]   = useState(DEFAULT_GRID);      // what is typed (strings ok)
  const [over, setOver]   = useState({});                // loanid -> typed override
  const [excl, setExcl]   = useState({});                // loanid -> true when out of the mark
  const [adj, setAdj]     = useState(ZERO_ADJ);          // { size:{}, collateral:{} } typed
  // Renewal overlay: the defaults (months, fee in PERCENT as typed) and each
  // loan's own { months, fee } — blank = the default, months 0 = won't renew.
  const [rDef, setRDef]   = useState({ months: "24", fee: "1.5" });
  const [rLoan, setRLoan] = useState({});
  const [saved, setSaved] = useState({ grid: DEFAULT_GRID, over: {}, excl: {}, adj: ZERO_ADJ,
                                       rDef: { months: "24", fee: "1.5" }, rLoan: {} });
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState("companyname");
  const [sortDir, setSortDir] = useState("asc");

  // ── load: everything the mark needs, in one read ─────────────────────────
  // Keyed on the run's id, not the run object: the page re-reads the run
  // while it polls, and a new object must not throw away typed spreads.
  const runId = run && run.id;
  useEffect(() => {
    if (!runId) return;
    let live = true;
    setBusy(true); setErr("");
    api.get("getPerformanceExport", { runid: runId, what: "mark" })
      .then(d => {
        if (!live) return;
        const g = { ...DEFAULT_GRID, ...(d.grid || {}) };
        const o = Object.fromEntries(Object.entries(d.overrides || {}).map(([k, v]) => [k, String(v)]));
        const x = Object.fromEntries((d.excluded || []).map(k => [String(k), true]));
        // No adjustment table yet = no adjustments (the rating spread alone),
        // so the mark matches what was saved before the script ran.
        const a = d.adjustments
          ? { size: { ...DEFAULT_ADJ.size, ...(d.adjustments.size || {}) },
              collateral: { ...DEFAULT_ADJ.collateral, ...(d.adjustments.collateral || {}) } }
          : ZERO_ADJ;
        const rd = d.renewal
          ? { months: String(d.renewal.months), fee: String(+(d.renewal.feePct * 100).toFixed(4)) }
          : { months: "24", fee: "1.5" };
        const rl = {};
        Object.entries(d.renewMonths || {}).forEach(([k, v]) => { rl[k] = { ...(rl[k] || {}), months: String(v) }; });
        Object.entries(d.renewFeePct || {}).forEach(([k, v]) => {
          rl[k] = { ...(rl[k] || {}), fee: String(+(v * 100).toFixed(4)) }; });
        setData(d); setGrid(g); setOver(o); setExcl(x); setAdj(a); setRDef(rd); setRLoan(rl);
        setSaved({ grid: g, over: o, excl: x, adj: a, rDef: rd, rLoan: rl }); setBusy(false);
      })
      .catch(e => { if (live) { setErr(e.message || String(e)); setBusy(false); } });
    return () => { live = false; };
  }, [api, runId]);

  // Only whole basis points take part; a half-typed value is ignored rather
  // than priced as zero.
  const numGrid = useMemo(() => Object.fromEntries(MARK_RATINGS.map(r =>
    [r, isBps(grid[r]) ? Number(grid[r]) : DEFAULT_GRID[r]])), [grid]);
  const numOver = useMemo(() => Object.fromEntries(Object.entries(over)
    .filter(([, v]) => isBps(v)).map(([k, v]) => [k, Number(v)])), [over]);
  const numAdj = useMemo(() => ({
    size: Object.fromEntries(SIZE_BUCKETS.map(b => [b, isBps(adj.size[b]) ? Number(adj.size[b]) : 0])),
    collateral: Object.fromEntries(COLLATERAL_PROFILES.map(b =>
      [b, isBps(adj.collateral[b]) ? Number(adj.collateral[b]) : 0])),
  }), [adj]);

  const isMonths = v => v !== "" && v !== null && v !== undefined && Number.isInteger(Number(v)) && Number(v) >= 0 && Number(v) <= 120;
  const isFeePct = v => v !== "" && v !== null && v !== undefined && isFinite(Number(v)) && Number(v) >= 0 && Number(v) <= 20;
  const numRenewal = useMemo(() => ({
    months: isMonths(rDef.months) ? Number(rDef.months) : 24,
    feePct: isFeePct(rDef.fee) ? Number(rDef.fee) / 100 : 0.015,
  }), [rDef]);

  const inputs = useMemo(() => data && {
    asof: data.asofdate, curvePoints: data.curve || [],
    // each loan carries its own renewal settings, if any
    loans: (data.loans || []).map(l => {
      const o = rLoan[l.loanid] || {};
      return { ...l,
        renewMonths: isMonths(o.months) ? Number(o.months) : undefined,
        renewFeePct: isFeePct(o.fee) ? Number(o.fee) / 100 : undefined };
    }),
    grid: numGrid, overrides: numOver, excluded: excl, adjustments: numAdj,
  }, [data, numGrid, numOver, excl, numAdj, rLoan]);

  const priced = useMemo(() => inputs ? priceBook(inputs) : null, [inputs]);
  // The same book IF it renews (see renewFlows in mark.math.mjs). Never
  // blended with the contractual mark: shown beside it.
  const pricedR = useMemo(() => inputs ? priceBook({ ...inputs, renewal: numRenewal }) : null,
    [inputs, numRenewal]);
  const renewedBy = useMemo(() => new Map((pricedR ? pricedR.rows : []).map(x => [x.loanid, x])), [pricedR]);
  const sens = useMemo(() => inputs
    ? SHIFTS.map(s => ({ s, p: priceBook({ ...inputs, shift: s, yields: false }).book.price,
                         pr: priceBook({ ...inputs, shift: s, yields: false, renewal: numRenewal }).book.price }))
    : [], [inputs, numRenewal]);

  const dirty = useMemo(() => {
    const g = MARK_RATINGS.some(r => String(grid[r]) !== String(saved.grid[r]));
    const keys = new Set([...Object.keys(over), ...Object.keys(saved.over)]);
    const o = [...keys].some(k => String(over[k] ?? "") !== String(saved.over[k] ?? ""));
    const xk = new Set([...Object.keys(excl), ...Object.keys(saved.excl)]);
    const x = [...xk].some(k => !!excl[k] !== !!saved.excl[k]);
    const a = ["size", "collateral"].some(d => Object.keys(adj[d]).some(k =>
      String(adj[d][k]) !== String(saved.adj[d][k])));
    const rd = String(rDef.months) !== String(saved.rDef.months) || String(rDef.fee) !== String(saved.rDef.fee);
    const rk = new Set([...Object.keys(rLoan), ...Object.keys(saved.rLoan)]);
    const rl = [...rk].some(k => String((rLoan[k] || {}).months ?? "") !== String((saved.rLoan[k] || {}).months ?? "")
                             || String((rLoan[k] || {}).fee ?? "") !== String((saved.rLoan[k] || {}).fee ?? ""));
    return g || o || x || a || rd || rl;
  }, [grid, over, excl, adj, rDef, rLoan, saved]);

  const save = async () => {
    if (!canSave || saving) return;
    const bad = MARK_RATINGS.filter(r => !isBps(grid[r]));
    const badAdj = ["size", "collateral"].flatMap(d => Object.keys(adj[d]).filter(k => !isBps(adj[d][k])));
    if (bad.length || badAdj.length) {
      setSaveMsg("Whole basis points only: " + bad.concat(badAdj).join(", ")); return;
    }
    const keys = new Set([...Object.keys(over), ...Object.keys(saved.over)]);
    const overrides = {};
    for (const k of keys) {
      const v = over[k] ?? "";
      if (String(v) === String(saved.over[k] ?? "")) continue;
      overrides[k] = isBps(v) ? Number(v) : null;
    }
    const excluded = {};
    for (const k of new Set([...Object.keys(excl), ...Object.keys(saved.excl)]))
      if (!!excl[k] !== !!saved.excl[k]) excluded[k] = !!excl[k];
    setSaving(true); setSaveMsg("");
    try {
      // Adjustments only once their table exists; before that they are a
      // what-if, and sending them would fail the whole save.
      // Renewal settings only once their tables exist, like the adjustments.
      const renew = {};
      for (const k of new Set([...Object.keys(rLoan), ...Object.keys(saved.rLoan)])) {
        const a = rLoan[k] || {}, b = saved.rLoan[k] || {};
        if (String(a.months ?? "") === String(b.months ?? "") && String(a.fee ?? "") === String(b.fee ?? "")) continue;
        const months = isMonths(a.months) ? Number(a.months) : null;
        const feePct = isFeePct(a.fee) ? Number(a.fee) / 100 : null;
        renew[k] = months === null && feePct === null ? null : { months, feePct };
      }
      await api.post("saveMarkSpreads", { grid: numGrid, overrides, excluded,
                                          ...(data.adjustments ? { adjustments: numAdj } : {}),
                                          ...(data.renewal ? { renewal: numRenewal, renew } : {}) });
      const cleanOver = Object.fromEntries(Object.entries(over).filter(([, v]) => isBps(v)));
      const cleanExcl = Object.fromEntries(Object.entries(excl).filter(([, v]) => v));
      setOver(cleanOver); setExcl(cleanExcl);
      setSaved({ grid: { ...grid }, over: cleanOver, excl: cleanExcl,
                 adj: data.adjustments ? adj : saved.adj,
                 rDef: data.renewal ? rDef : saved.rDef, rLoan: data.renewal ? rLoan : saved.rLoan });
      setSaveMsg("Saved.");
    } catch (e) { setSaveMsg(e.message || String(e)); }
    setSaving(false);
  };
  const revert = () => { setGrid(saved.grid); setOver(saved.over); setExcl(saved.excl);
                         setAdj(saved.adj); setRDef(saved.rDef); setRLoan(saved.rLoan); setSaveMsg(""); };
  const smallIn = (val, onChange, ok, changed, placeholder, w = 44) => (
    <input value={val ?? ""} placeholder={placeholder} inputMode="decimal" onChange={onChange}
           style={{ width:w, padding:"3px 5px", fontSize:12, textAlign:"right", borderRadius:4,
                    fontVariantNumeric:"tabular-nums",
                    border:"1px solid " + (val === "" || val === undefined || ok ? HAIRLINE_FIRM : "#d14343"),
                    background: changed ? "#fff8e1" : val !== "" && val !== undefined ? B.cyanLight : SURFACE }} />
  );
  const setLoanR = (id, k, v) => setRLoan(m => {
    const cur = { ...(m[id] || {}), [k]: v };
    if ((cur.months ?? "") === "" && (cur.fee ?? "") === "") { const n = { ...m }; delete n[id]; return n; }
    return { ...m, [id]: cur };
  });
  const adjInput = (dim, b) => (
    <input value={adj[dim][b] ?? ""} inputMode="numeric"
           onChange={e => setAdj(a => ({ ...a, [dim]: { ...a[dim], [b]: e.target.value.replace(/[^\d-]/g, "") } }))}
           style={{ padding:"6px 8px", fontSize:13, borderRadius:6, width:"100%",
                    boxSizing:"border-box", fontVariantNumeric:"tabular-nums",
                    border:"1px solid " + (isBps(adj[dim][b]) ? HAIRLINE_FIRM : "#d14343"),
                    background: String(adj[dim][b]) !== String(saved.adj[dim][b]) ? "#fff8e1" : SURFACE }} />
  );
  // How a loan's spread was built, for the Spread cell's tooltip.
  const spreadNote = r => {
    const p = r.spreadParts || {};
    if (p.override !== undefined) return `Override ${p.override} bps (beats rating, size and collateral). Clear it to go back.`;
    return `${p.baseFrom || "rating"} ${p.base} ${p.sizeAdj >= 0 ? "+" : "−"} ${Math.abs(p.sizeAdj)} size (${SIZE_LABEL[p.size] || p.size})`
         + ` ${p.collateralAdj >= 0 ? "+" : "−"} ${Math.abs(p.collateralAdj)} collateral (${p.collateral})`
         + ` = ${r.spreadBps} bps. Type a spread to override this loan.`;
  };
  const toggleExcl = id => setExcl(x => ({ ...x, [id]: !x[id] }));
  const setAllIn = on => setExcl(on ? {} : Object.fromEntries((priced ? priced.rows : [])
    .filter(r => r.price !== null).map(r => [r.loanid, true])));

  // ── table rows ───────────────────────────────────────────────────────────
  // Margin in bps on floating loans only: a fixed loan's "margin" is its whole
  // coupon and would sort and compare as if it were a 900 bps spread.
  const withMargin = useMemo(() => (priced ? priced.rows : []).map(x => {
    const rr = renewedBy.get(x.loanid);
    const own = rLoan[x.loanid] || {};
    const mo = isMonths(own.months) ? Number(own.months) : numRenewal.months;
    return { ...x, marginBps: x.margin == null || x.fixed ? null : Math.round(x.margin * 10000),
             priceR: rr ? rr.price : null, mvR: rr ? rr.mv : null,
             renewMonthsUsed: mo, renewFeeUsed: isFeePct(own.fee) ? Number(own.fee) / 100 : numRenewal.feePct,
             renewSort: mo };
  }), [priced, renewedBy, rLoan, numRenewal]);
  const bkMargin = useMemo(() => {
    let w = 0, p = 0;
    for (const x of withMargin) if (!x.excluded && x.price !== null && x.marginBps !== null) {
      w += x.marginBps * x.par; p += x.par; }
    return p > 0 ? Math.round(w / p) : null;
  }, [withMargin]);
  const bkCommit = useMemo(() => withMargin.reduce((t, x) =>
    t + (!x.excluded && x.price !== null ? (x.loanCommitment || 0) : 0), 0), [withMargin]);
  const rows = useMemo(() => {
    if (!priced) return [];
    const needle = q.trim().toLowerCase();
    const r = withMargin.filter(x => !needle
      || String(x.companyname || "").toLowerCase().includes(needle)
      || String(x.loannumber || "").includes(needle));
    const dir = sortDir === "asc" ? 1 : -1;
    return r.sort((a, b) => {
      // Unpriced loans (no par) always sink to the bottom.
      if ((a.price === null) !== (b.price === null)) return a.price === null ? 1 : -1;
      const av = a[sortKey], bv = b[sortKey];
      if (typeof av === "number" || typeof bv === "number")
        return ((av ?? -Infinity) - (bv ?? -Infinity)) * dir;
      return String(av ?? "").localeCompare(String(bv ?? "")) * dir;
    });
  }, [priced, withMargin, q, sortKey, sortDir]);

  const onSort = k => {
    if (k === sortKey) setSortDir(d => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir(k === "companyname" || k === "loannumber" ? "asc" : "desc"); }
  };

  const csv = () => exportCSV(rows.map(r => ({
      in_mark: r.excluded ? "no" : "yes",
      company: r.companyname, loan: r.loannumber, rating: r.rating,
      borrower_commitment: r.commitment, loan_commitment: r.loanCommitment,
      size_bucket: r.sizeBucket, collateral_profile: r.collateralProfile,
      ar_share: r.arShare, inventory_share: r.invShare,
      margin: r.margin, rate_type: r.fixed ? "Fixed" : "Floating", margin_bps: r.marginBps,
      rating_spread_bps: r.spreadParts && r.spreadParts.base, size_adj_bps: r.spreadParts && r.spreadParts.sizeAdj,
      collateral_adj_bps: r.spreadParts && r.spreadParts.collateralAdj,
      renew_months: r.renewMonthsUsed, renew_fee_pct: r.renewFeeUsed,
      price_with_renewal: r.priceR, market_value_with_renewal: r.mvR,
      par: r.par, accrued: r.accrued, spread_bps: r.spreadBps, spread_from: r.spreadFrom,
      asset_yield: r.assetYield, mark_yield: r.markYield, wal_years: r.wal,
      price: r.price, market_value: r.mv, premium: r.premium })),
    ["in_mark","company","loan","rating","borrower_commitment","loan_commitment","size_bucket","collateral_profile","ar_share",
     "inventory_share","margin","rate_type","margin_bps","rating_spread_bps","size_adj_bps","collateral_adj_bps","par","accrued","spread_bps","spread_from","asset_yield",
     "mark_yield","wal_years","price","market_value","premium","renew_months","renew_fee_pct","price_with_renewal","market_value_with_renewal"],
    `portfolio_mark_${data ? data.asofdate : ""}.csv`);

  if (busy) return <Spinner />;
  if (err)  return <ErrorMsg msg={err} />;
  if (!data || !priced) return null;

  const bk = priced.book;
  const unpriced = priced.rows.filter(r => r.price === null).length;
  const curveNote = !data.curve || !data.curve.length
    ? "No SOFR forward curve was found for this run, so every flow is discounted at the spread alone. Upload the curve and recalculate."
    : (data.curvedate && data.forwardcurvedate && data.curvedate !== data.forwardcurvedate
        ? `The run was built on the ${fmtDate(data.forwardcurvedate)} curve, which has no SOFR rows; discounting uses the ${fmtDate(data.curvedate)} SOFR curve instead.`
        : "");

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

      {/* ── what this is priced off ─────────────────────────────────────── */}
      <div style={{ fontSize:12, color:INK_MUTED, lineHeight:1.6 }}>
        Clean price per 100 of par: projected net cash from this run, discounted at forward SOFR
        ({data.curvedate ? fmtDate(data.curvedate) + " curve" : "no curve"}) plus each loan&rsquo;s
        spread, less accrued interest. As of {fmtDate(data.asofdate)}.
        {!canSave && <> Spreads typed here are a what-if; only an administrator can save them.</>}
      </div>
      {(curveNote || (data.settingsError && data.settingsError !== "excluded-missing")) && (
        <div style={{ padding:"9px 12px", borderRadius:6, fontSize:12, lineHeight:1.6,
                      background:"#fff8e1", border:"1px solid #f0c000", color:"#6b4e00" }}>
          {curveNote}
          {data.settingsError && data.settingsError !== "excluded-missing" && <> The spread tables are not set up yet (run
            sql/2026-11-03_portfolio_mark.sql), so the placeholder grid is in use and cannot be saved.</>}
        </div>
      )}

      {/* ── headline ───────────────────────────────────────────────────── */}
      <div style={{ display:"grid", gap:12, gridTemplateColumns:"repeat(auto-fit, minmax(170px, 1fr))" }}>
        <Tile label="Book price" value={price(bk.price)} big
              note={bk.price == null ? "" : bk.price >= 100 ? "premium to par" : "discount to par"} />
        <Tile label="Market value (clean)" value={money(bk.mv)}
              note={`${bk.loans} loan${bk.loans === 1 ? "" : "s"} in the mark`
                    + (bk.excluded ? ` · ${bk.excluded} excluded` : "")} />
        <Tile label="Par" value={money(bk.par)} note={`accrued ${money(bk.accrued)}`} />
        <Tile label="Premium / (discount)" value={money(bk.premium)} />
        <Tile label="Asset yield → mark yield" value={`${pct(bk.assetYield)} → ${pct(bk.markYield)}`}
              note="bought at par vs at the mark" />
        <Tile label="Weighted avg life" value={yrs(bk.wal) + " yrs"} />
        <Tile label="With renewal" value={pricedR ? price(pricedR.book.price) : "—"}
              note={pricedR ? `${money(pricedR.book.mv)} · +${(pricedR.book.price - bk.price).toFixed(3)} vs contractual` : ""} />
      </div>

      {/* ── sensitivity ────────────────────────────────────────────────── */}
      <Card style={{ padding:"12px 14px" }}>
        <PanelTitle title="Book price if spreads move" />
        <div style={{ display:"grid", gridTemplateColumns:`repeat(${SHIFTS.length}, 1fr)`, gap:6 }}>
          {sens.map(({ s, p }) => (
            <div key={s} style={{ textAlign:"center", padding:"8px 4px", borderRadius:6,
                                  background: s === 0 ? B.cyanLight : SURFACE_MUTED,
                                  border: "1px solid " + (s === 0 ? B.cyan : HAIRLINE) }}>
              <div style={{ fontSize:11, color:INK_MUTED }}>{s === 0 ? "as marked" : (s > 0 ? "+" : "") + s + " bps"}</div>
              <div style={{ fontSize:16, fontWeight: s === 0 ? 700 : 600, color:INK_PRIMARY,
                            fontVariantNumeric:"tabular-nums" }}>{price(p)}</div>
            </div>
          ))}
        </div>
        <div style={{ display:"grid", gridTemplateColumns:`repeat(${SHIFTS.length}, 1fr)`, gap:6, marginTop:6 }}>
          {sens.map(({ s, pr }) => (
            <div key={s} style={{ textAlign:"center", padding:"4px", fontSize:12, color:INK_MUTED,
                                  fontVariantNumeric:"tabular-nums" }}
                 title="Book price with every loan renewed on its renewal settings">
              with renewal <strong style={{ color:INK_PRIMARY }}>{price(pr)}</strong>
            </div>
          ))}
        </div>
        <div style={{ fontSize:11, color:INK_MUTED, marginTop:8 }}>
          Every loan&rsquo;s spread moved by the same amount. Wider spreads = a buyer wants more yield = a lower price.
        </div>
      </Card>

      {/* ── the spread grid ────────────────────────────────────────────── */}
      <Card style={{ padding:"12px 14px" }}>
        {/* Save / Revert cover everything typed on this tab: the grid, each
            loan's spread and which loans are in the mark. */}
        <PanelTitle title="Discount spread by risk rating (bps over SOFR)" actions={
          <span style={{ display:"flex", gap:8, alignItems:"center" }}>
            {saveMsg && <span style={{ fontSize:12, color: saveMsg === "Saved." ? "#0F6E56" : "#993C1D" }}>{saveMsg}</span>}
            {dirty && <Btn onClick={revert}>Revert</Btn>}
            {canSave && <Btn variant="primary" onClick={save}
                             disabled={!dirty || saving || !!data.settingsError}
                             title={data.settingsError === "excluded-missing"
                               ? "Run sql/2026-11-03_portfolio_mark_exclude.sql to save — the table cannot hold excluded loans yet."
                               : undefined}>
              {saving ? "Saving…" : "Save mark"}</Btn>}
          </span>} />
        <div style={{ display:"grid", gap:8, gridTemplateColumns:"repeat(auto-fit, minmax(84px, 1fr))" }}>
          {MARK_RATINGS.map(r => (
            <label key={r} style={{ display:"flex", flexDirection:"column", gap:3, fontSize:11, color:INK_MUTED }}>
              {r === "Unrated" ? "Unrated" : "Rating " + r}
              <input value={grid[r] ?? ""} inputMode="numeric"
                     onChange={e => setGrid(g => ({ ...g, [r]: e.target.value.replace(/[^\d-]/g, "") }))}
                     style={{ padding:"6px 8px", fontSize:13, borderRadius:6, width:"100%",
                              boxSizing:"border-box", fontVariantNumeric:"tabular-nums",
                              border:"1px solid " + (isBps(grid[r]) ? HAIRLINE_FIRM : "#d14343"),
                              background: String(grid[r]) !== String(saved.grid[r]) ? "#fff8e1" : SURFACE }} />
            </label>
          ))}
        </div>
        <div style={{ fontSize:11, color:INK_MUTED, marginTop:8 }}>
          Spread = rating + size + collateral (below). A loan&rsquo;s own spread in the table beats
          all three. Unrated covers loans with no rating or one not on this list. 100 bps = 1.00%.
        </div>
      </Card>

      {/* ── renewal overlay ───────────────────────────────────────────── */}
      <Card style={{ padding:"12px 14px" }}>
        <PanelTitle title="If loans renew (the With renewal price)" />
        {!data.renewal && (
          <div style={{ fontSize:12, color:"#6b4e00", background:"#fff8e1", border:"1px solid #f0c000",
                        borderRadius:6, padding:"7px 10px", marginBottom:10 }}>
            Not set up yet — run sql/2026-11-03_portfolio_mark_renewal.sql. Until then these are a what-if and are not saved.
          </div>
        )}
        <div style={{ display:"flex", gap:16, alignItems:"flex-end", flexWrap:"wrap" }}>
          <label style={{ display:"flex", flexDirection:"column", gap:3, fontSize:11, color:INK_MUTED }}>
            Extension (months)
            {smallIn(rDef.months, e => setRDef(d => ({ ...d, months: e.target.value.replace(/[^\d]/g, "") })),
                     isMonths(rDef.months), String(rDef.months) !== String(saved.rDef.months), "24", 90)}
          </label>
          <label style={{ display:"flex", flexDirection:"column", gap:3, fontSize:11, color:INK_MUTED }}>
            Renewal fee (% of commitment)
            {smallIn(rDef.fee, e => setRDef(d => ({ ...d, fee: e.target.value.replace(/[^\d.]/g, "") })),
                     isFeePct(rDef.fee), String(rDef.fee) !== String(saved.rDef.fee), "1.5", 90)}
          </label>
          <div style={{ flex:1, minWidth:280, fontSize:11, color:INK_MUTED, lineHeight:1.6 }}>
            The defaults for every loan; a loan&rsquo;s own extension and fee in the table beat them, and an
            extension of <strong>0</strong> means it won&rsquo;t renew. At maturity the balloon is replaced by the fee
            (on the loan&rsquo;s commitment, our share); the balance is carried flat at forward SOFR + its current
            margin plus its recurring fees, and repaid at the end of the extension. The schedule itself is never
            changed &mdash; this is the Mark&rsquo;s view only, shown beside the contractual price, never in it.
          </div>
        </div>
      </Card>

      {/* ── size and collateral adjustments ───────────────────────────── */}
      <Card style={{ padding:"12px 14px" }}>
        <PanelTitle title="Adjustments added to the rating spread (bps)" />
        {!data.adjustments && (
          <div style={{ fontSize:12, color:"#6b4e00", background:"#fff8e1", border:"1px solid #f0c000",
                        borderRadius:6, padding:"7px 10px", marginBottom:10 }}>
            Not set up yet — run sql/2026-11-03_portfolio_mark_adjustments.sql. Until then the mark uses
            the rating spread alone; anything typed here is a what-if and is not saved.
          </div>
        )}
        <div style={{ display:"grid", gap:18, gridTemplateColumns:"repeat(auto-fit, minmax(320px, 1fr))" }}>
          <div>
            <div style={{ fontSize:11, fontWeight:600, color:INK_PRIMARY, marginBottom:6 }}>
              Deal size — by the borrower&rsquo;s total commitment (all its loans)</div>
            <div style={{ display:"grid", gap:8, gridTemplateColumns:"repeat(4, 1fr)" }}>
              {SIZE_BUCKETS.map(b => (
                <label key={b} style={{ display:"flex", flexDirection:"column", gap:3, fontSize:11, color:INK_MUTED }}>
                  {SIZE_LABEL[b]}{adjInput("size", b)}
                </label>
              ))}
            </div>
          </div>
          <div>
            <div style={{ fontSize:11, fontWeight:600, color:INK_PRIMARY, marginBottom:6 }}>
              Collateral — from the latest posted borrowing base</div>
            <div style={{ display:"grid", gap:8, gridTemplateColumns:"repeat(5, 1fr)" }}>
              {COLLATERAL_PROFILES.map(b => (
                <label key={b} style={{ display:"flex", flexDirection:"column", gap:3, fontSize:11, color:INK_MUTED }}>
                  <span style={{ whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }} title={b}>{b}</span>
                  {adjInput("collateral", b)}
                </label>
              ))}
            </div>
          </div>
        </div>
        <div style={{ fontSize:11, color:INK_MUTED, marginTop:8, lineHeight:1.6 }}>
          Collateral profile: <strong>Cash flow</strong> when the term is CF; otherwise from the latest posted
          collateral position on or before the as-of date — <strong>AR-backed</strong> when receivables are at
          least 75% of collateral value, <strong>Inventory-heavy</strong> when inventory is at least 50%,
          <strong> Mixed</strong> otherwise. <strong>No collateral data</strong> is an ABL loan with nothing posted.
          Negative = tighter (a higher price), positive = wider.
        </div>
      </Card>

      {/* ── the loans ──────────────────────────────────────────────────── */}
      <Card style={{ padding:0, overflow:"hidden" }}>
        <div style={{ display:"flex", gap:10, alignItems:"center", padding:"10px 12px", flexWrap:"wrap" }}>
          <div style={{ width:260 }}><SearchBar value={q} onChange={setQ} placeholder="Company or loan…" /></div>
          {unpriced > 0 && <span style={{ fontSize:12, color:INK_MUTED }}>
            {unpriced === 1 ? "1 loan has" : unpriced + " loans have"} no par at the as-of date (paid off or charged off) and {unpriced === 1 ? "is" : "are"} listed last, unpriced.
          </span>}
          <div style={{ marginLeft:"auto", display:"flex", gap:8, alignItems:"center" }}>
            {bk.excluded > 0 && <span style={{ fontSize:12, color:INK_MUTED }}>
              {bk.excluded} excluded from the book</span>}
            <Btn onClick={() => setAllIn(true)} disabled={!bk.excluded}>Include all</Btn>
            <Btn onClick={csv} disabled={!rows.length}>Export CSV</Btn>
          </div>
        </div>
        <div style={{ overflowX:"auto", maxHeight:"62vh" }}>
          <table style={{ borderCollapse:"collapse", width:"100%", fontSize:12, fontVariantNumeric:"tabular-nums" }}>
            <thead>
              <tr>
                <th style={{ position:"sticky", top:0, zIndex:1, background:SURFACE_MUTED, padding:"8px 6px 8px 12px",
                             fontSize:10.5, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.04em",
                             color:INK_PRIMARY, borderBottom:"1.5px solid " + HAIRLINE_FIRM, textAlign:"left" }}
                    title="Untick to take a loan out of the book. It stays listed and priced, greyed out.">In</th>
                {[["companyname","Company",false],["loannumber","Loan",false],["rating","Rating",false],
                  ["sizeBucket","Size",false],["collateralProfile","Collateral",false],
                  ["loanCommitment","Commitment",true],["par","Par",true],["marginBps","Margin (bps)",true],["spreadBps","Spread (bps)",true],["assetYield","Asset yield",true],
                  ["markYield","Mark yield",true],["wal","WAL (yrs)",true],
                  ["renewSort","Renewal (mo · fee %)",true],["price","Price",true],["priceR","w/ renewal",true],
                  ["mv","Market value",true],["premium","Premium",true]].map(([k, l, n]) => (
                  <th key={k} onClick={() => onSort(k)}
                      style={{ position:"sticky", top:0, zIndex:1, background:SURFACE_MUTED, cursor:"pointer",
                               padding:"8px 10px", textAlign: n ? "right" : "left", whiteSpace:"nowrap",
                               fontSize:10.5, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.04em",
                               color: sortKey === k ? B.cyanDark : INK_PRIMARY,
                               borderBottom:"1.5px solid " + HAIRLINE_FIRM }}>
                    {l}{sortKey === k ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const dim = r.price === null || r.excluded;
                const td = { padding:"6px 10px", borderBottom:"1px solid " + HAIRLINE,
                             color: dim ? INK_MUTED : INK_PRIMARY, whiteSpace:"nowrap",
                             background: r.excluded ? SURFACE_MUTED : undefined };
                const exChanged = !!excl[r.loanid] !== !!saved.excl[r.loanid];
                const n = { ...td, textAlign:"right" };
                const ov = over[r.loanid] ?? "";
                return (
                  <tr key={r.loanid}>
                    <td style={{ ...td, padding:"6px 6px 6px 12px",
                                 background: exChanged ? "#fff8e1" : td.background }}>
                      {r.price !== null && (
                        <input type="checkbox" checked={!r.excluded} onChange={() => toggleExcl(r.loanid)}
                               title={r.excluded ? "Excluded — tick to put it back in the mark"
                                                 : "In the mark — untick to exclude it"}
                               style={{ width:15, height:15, cursor:"pointer" }} />
                      )}
                    </td>
                    <td style={{ ...td, textDecoration: r.excluded ? "line-through" : "none" }}>{r.companyname}</td>
                    <td style={td}>{r.loannumber}</td>
                    <td style={td}>{r.rating || <span style={{ color:INK_MUTED }}>—</span>}</td>
                    <td style={td} title={r.commitment
                      ? `Borrower commitment ${money(r.commitment)} across all its loans`
                        + (r.loanCommitment != null ? ` (this loan ${money(r.loanCommitment)})` : "")
                      : "No commitment on file for this borrower"}>
                      {SIZE_LABEL[r.sizeBucket] || r.sizeBucket}</td>
                    <td style={td} title={r.collateralProfile === "Cash flow" ? "The term's collateral type is CF"
                      : r.arShare == null ? "No posted collateral position by the as-of date"
                      : `AR ${(r.arShare * 100).toFixed(0)}% · inventory ${(r.invShare * 100).toFixed(0)}% of collateral value, posted ${fmtDate(r.collateralAsof)}`}>
                      {r.collateralProfile}</td>
                    <td style={n} title={r.loanCommitment == null ? "No funded term by the as-of date"
                      : (r.loanCommitment > 0 && r.par > 0
                          ? `${Math.round(r.par / r.loanCommitment * 100)}% drawn (par ÷ commitment). `
                          : "")
                        + "Revolvers and delayed draw loans: the line. Term loans: what is still out. "
                        + "Paid off or charged off: zero."}>
                      {r.loanCommitment == null ? <span style={{ color:INK_MUTED }}>—</span> : money(r.loanCommitment)}
                    </td>
                    <td style={n}>{money(r.par)}</td>
                    <td style={n} title={r.margin == null ? "No margin on the term in force at the as-of date"
                      : r.fixed ? `Fixed-rate loan: ${(r.margin * 100).toFixed(2)}% is the whole coupon, not a spread over SOFR — not comparable to the discount spread.`
                      : `Margin over SOFR on the term in force at the as-of date. ${r.marginBps > r.spreadBps
                          ? `${r.marginBps - r.spreadBps} bps above its discount spread: a premium before fees.`
                          : r.marginBps < r.spreadBps
                            ? `${r.spreadBps - r.marginBps} bps below its discount spread: a discount unless fees make it up.`
                            : "Equal to its discount spread."}`}>
                      {r.margin == null ? <span style={{ color:INK_MUTED }}>—</span>
                        : r.fixed ? <span>{(r.margin * 100).toFixed(2)}% <span style={{ fontSize:10, color:INK_MUTED }}>fixed</span></span>
                        : r.marginBps}
                    </td>
                    <td style={n} title={spreadNote(r)}>
                      <input value={ov} inputMode="numeric" placeholder={String(r.spreadBps)}
                             onChange={e => setOver(o => ({ ...o, [r.loanid]: e.target.value.replace(/[^\d-]/g, "") }))}
                             style={{ width:64, padding:"3px 6px", fontSize:12, textAlign:"right", borderRadius:4,
                                      fontVariantNumeric:"tabular-nums",
                                      border:"1px solid " + (ov === "" || isBps(ov) ? HAIRLINE_FIRM : "#d14343"),
                                      background: String(ov) !== String(saved.over[r.loanid] ?? "") ? "#fff8e1"
                                                : ov !== "" ? B.cyanLight : SURFACE }} />
                    </td>
                    <td style={n}>{pct(r.assetYield)}</td>
                    <td style={n}>{pct(r.markYield)}</td>
                    <td style={n}>{yrs(r.wal)}</td>
                    <td style={{ ...n, whiteSpace:"nowrap" }}
                        title={"Blank = the default (" + numRenewal.months + " months, "
                               + (numRenewal.feePct * 100).toFixed(2) + "%). Months 0 = this loan won't renew."}>
                      {smallIn((rLoan[r.loanid] || {}).months,
                               e => setLoanR(r.loanid, "months", e.target.value.replace(/[^\d]/g, "")),
                               isMonths((rLoan[r.loanid] || {}).months),
                               String((rLoan[r.loanid] || {}).months ?? "") !== String((saved.rLoan[r.loanid] || {}).months ?? ""),
                               String(numRenewal.months), 38)}
                      <span style={{ color:INK_MUTED, margin:"0 3px" }}>·</span>
                      {smallIn((rLoan[r.loanid] || {}).fee,
                               e => setLoanR(r.loanid, "fee", e.target.value.replace(/[^\d.]/g, "")),
                               isFeePct((rLoan[r.loanid] || {}).fee),
                               String((rLoan[r.loanid] || {}).fee ?? "") !== String((saved.rLoan[r.loanid] || {}).fee ?? ""),
                               String(+(numRenewal.feePct * 100).toFixed(2)), 42)}
                    </td>
                    <td style={{ ...n, fontWeight: r.excluded ? 400 : 700,
                                 color: dim ? INK_MUTED : r.price >= 100 ? "#0F6E56" : "#993C1D" }}>{price(r.price)}</td>
                    <td style={{ ...n, color: dim ? INK_MUTED : INK_PRIMARY }}
                        title={r.mvR == null ? "" : `Market value with renewal ${money(r.mvR)} (${r.mvR - r.mv >= 0 ? "+" : ""}${money(r.mvR - r.mv)} vs contractual)`}>
                      {price(r.priceR)}</td>
                    <td style={n}>{money(r.mv)}</td>
                    <td style={n}>{money(r.premium)}</td>
                  </tr>
                );
              })}
              <tr style={{ fontWeight:700 }}>
                {(() => {
                  const td = { padding:"8px 10px", borderTop:"2px solid " + HAIRLINE_FIRM, background:SURFACE_MUTED };
                  const n = { ...td, textAlign:"right" };
                  return <>
                    <td style={td} colSpan={6}>Book{bk.excluded ? ` (excluding ${bk.excluded})` : ""}</td>
                    <td style={n} title="Commitment of the loans in the mark">{money(bkCommit)}</td>
                    <td style={n}>{money(bk.par)}</td>
                    <td style={n} title="Par-weighted margin of the floating loans in the mark">
                      {bkMargin == null ? "" : bkMargin}</td>
                    <td style={n} />
                    <td style={n}>{pct(bk.assetYield)}</td>
                    <td style={n}>{pct(bk.markYield)}</td>
                    <td style={n}>{yrs(bk.wal)}</td>
                    <td style={n} />
                    <td style={n}>{price(bk.price)}</td>
                    <td style={n}>{pricedR ? price(pricedR.book.price) : ""}</td>
                    <td style={n}>{money(bk.mv)}</td>
                    <td style={n}>{money(bk.premium)}</td>
                  </>;
                })()}
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      <div style={{ fontSize:11, color:INK_MUTED, lineHeight:1.6 }}>
        <strong>Asset yield</strong> is what a loan returns if bought at par (plus accrued);
        <strong> mark yield</strong> is what it returns bought at the mark. A loan prices over 100
        when its asset yield is above its mark yield — it pays more than the market asks for its grade.
        Both are annual effective rates on the projected cash, like the IRRs. Book figures are pooled
        over every priced loan&rsquo;s cash, not averaged. An unticked loan is still priced in its row
        but left out of every book figure and the sensitivity strip. The Mark Proof workbook in doc/ computes the
        same prices with plain formulas.
      </div>
    </div>
  );
};

const Tile = ({ label, value, note, big }) => (
  <Card style={{ padding:"12px 14px" }}>
    <div style={{ fontSize:11, color:INK_MUTED, textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:4 }}>{label}</div>
    <div style={{ fontSize: big ? 24 : 18, fontWeight: big ? 700 : 600, color: big ? B.cyanDark : INK_PRIMARY,
                  fontVariantNumeric:"tabular-nums" }}>{value}</div>
    {note && <div style={{ fontSize:11, color:INK_MUTED, marginTop:3 }}>{note}</div>}
  </Card>
);
